import { toast } from 'react-toastify';

import { ReminderModal } from '@ReactComponents/TransactionsPage/TransactionsModals/ReminderModal';

import { AppThunkDispatch } from '@Reducers/index';

import { SplitApi } from '@ClientApi/split';
import { ISearchAccountingSyncExpensesResponse, IUnapproveExpenseData, TransactionsApi } from '@ClientApi/transactionsApi';
import { IExpenseData } from '@Shared/expense_data';
import { IExpenseCombinedModel } from '@Shared/helpers/dataMappers/expenseMappers';
import { ISearchTransactionsParams } from '@Shared/interfaces/Filters';

import { ShowModal } from './modalActions';

export const SET_TRANSACTIONS_LOADING = 'SET_TRANSACTIONS_LOADING';
export const RESET_TRANSACTIONS_LOADED_TIMESTAMP = 'RESET_TRANSACTIONS_LOADED_TIMESTAMP';
export const RESET_TRANSACTIONS_UPDATED_TIMESTAMP = 'RESET_TRANSACTIONS_UPDATED_TIMESTAMP';
export const RESET_TRANSACTIONS = 'RESET_TRANSACTIONS';
export const SELECT_TRANSACTION = 'SELECT_TRANSACTION';
export const CLEAR_SELECTED_TRANSACTION = 'CLEAR_SELECTED_TRANSACTION';
export const SET_ACCOUNTING_TRANSACTIONS_COUNT = 'SET_ACCOUNTING_TRANSACTIONS_COUNT';

export class SetTransactionsLoading {
  readonly type = SET_TRANSACTIONS_LOADING;

  constructor(public payload: boolean) {}
}

export class ResetTransactionsLoadedTimestamp {
  readonly type = RESET_TRANSACTIONS_LOADED_TIMESTAMP;
}

export class ResetTransactionsUpdatedTimestamp {
  readonly type = RESET_TRANSACTIONS_UPDATED_TIMESTAMP;
}

export class ResetTransactions {
  readonly type = RESET_TRANSACTIONS;
}

export class SelectTransaction {
  readonly type = SELECT_TRANSACTION;

  constructor(public payload: IExpenseCombinedModel | null) {}
}

export class ClearSelectedTransaction {
  readonly type = CLEAR_SELECTED_TRANSACTION;
}

export class SetAccountingTransactionsCount {
  readonly type = SET_ACCOUNTING_TRANSACTIONS_COUNT;

  constructor(public payload: ISearchAccountingSyncExpensesResponse) {}
}

/**
 * Remove params from sections that don't support them
 *
 * @param {string} section
 * @param {ISearchTransactionsParams} params
 * @return {*}
 */
export function sanitizeSearchParams(section: string, params: ISearchTransactionsParams) {
  const sanitizedParams = { ...params };
  sanitizedParams.filters = { ...params.filters };
  if (section !== 'pending') {
    delete sanitizedParams.filters.status;
  }

  return sanitizedParams;
}

export const approveTransaction = (expense: IExpenseCombinedModel) => async (dispatch: AppThunkDispatch) => {
  await TransactionsApi.approve(expense.id);
};

export const denyTransaction =
  (expense: IExpenseCombinedModel, data: Parameters<typeof TransactionsApi.deny>[1]) => async (dispatch: AppThunkDispatch) => {
    await TransactionsApi.deny(expense.id, data);
  };

export const skipTransaction = (expense: IExpenseCombinedModel) => async (dispatch: AppThunkDispatch) => {
  await TransactionsApi.skip(expense);
};

export const clearSelectedTransaction = () => (dispatch: AppThunkDispatch) => {
  dispatch(new ClearSelectedTransaction());
};

export const unapproveTransaction = (expense: IExpenseCombinedModel, data: IUnapproveExpenseData) => async (dispatch: AppThunkDispatch) => {
  await TransactionsApi.unapprove(expense.id, data);
};

export const deleteTransaction = (expense: IExpenseCombinedModel) => async (dispatch: AppThunkDispatch) => {
  if (expense.split_transaction_id) {
    await SplitApi.deleteSplits(expense.split_transaction_id);
  } else {
    await TransactionsApi.deleteOne(expense.id);
  }
};

export const queueTransaction = (payload: IExpenseData) => async (dispatch: AppThunkDispatch) => {
  try {
    await TransactionsApi.queueWithoutToastWrapper(payload);
  } catch (error) {
    const data = JSON.parse(error.message);
    toast.error(data.error);
  }
};

export const submitTransaction = (payload: IExpenseData) => async (dispatch: AppThunkDispatch) => {
  try {
    await TransactionsApi.submitWithoutToastWrapper(payload);
  } catch (error) {
    const data = JSON.parse(error.message);
    toast.error(data.error);
  }
};

export const openReminderModal = (userId: string) => async (dispatch: AppThunkDispatch) => {
  dispatch(
    new ShowModal(ReminderModal, {
      onSuccessfulReminder: () => {},
      userId
    })
  );
};

export const unskipTransaction = (expense: IExpenseCombinedModel) => async (dispatch: AppThunkDispatch) => {
  await TransactionsApi.unskip(expense);
};

export type TransactionsActions =
  | SetTransactionsLoading
  | ResetTransactionsLoadedTimestamp
  | ResetTransactionsUpdatedTimestamp
  | ResetTransactions
  | SelectTransaction
  | ClearSelectedTransaction
  | SetAccountingTransactionsCount;
